import {
  Button,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import HeroImageTertiary from '@/assets/group-rv-bookings/airstreams-forest-campsite.jpg';
import HeroImagePrimary from '@/assets/group-rv-bookings/construction-site-aerial.jpg';
import HeroImageQuaternary from '@/assets/group-rv-bookings/music-festival-aerial.jpg';
import HeroImageSecondary from '@/assets/group-rv-bookings/wedding-couple-forest-aisle.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';

import styles from './GroupRvHero.module.css';

type TGroupRvHeroProps = {
  eyebrow?: React.ReactNode;
  headline?: React.ReactNode;
  body?: React.ReactNode;
  images?: { src: string }[];
};

export const GroupRvHero: React.FC<TGroupRvHeroProps> = ({ eyebrow, headline, body, images }) => {
  const intl = useIntl();

  const defaultCtaLabel = intl.formatMessage({
    id: '/6Jm5F',
    defaultMessage: 'Submit a group booking enquiry',
    description: 'Group bookings primary CTA',
  });

  return (
    <section className="py-10 bg-white xl:pt-10 xl:pb-0  lg:py-10 lg:px-12">
      <div className="flex justify-center">
        <MaxWidth className="lg:flex lg:flex-row lg:items-start lg:gap-16">
          {/* Copy column */}
          <div className="max-w-xl lg:flex-1">
            <Text
              variant={ETextStyleVariant.SmallBold}
              className={`mb-3 xl:pt-10 text-gray-70 ${styles.heroEyebrow}`}>
              {eyebrow ?? (
                <FormattedMessage
                  id="Nf29/M"
                  defaultMessage="Booking multiple RVs made easy."
                  description="Group bookings hero eyebrow"
                />
              )}
            </Text>

            <Heading
              variant={EHeadingStyleVariant.H2}
              className={`mb-5 leading-tight text-gray-900 font-[abril-titling] ${styles.heroHeading}`}>
              {headline ?? (
                <FormattedMessage
                  id="MhdNzO"
                  defaultMessage="Group RV rentals for events, workforces, and film productions."
                  description="Group bookings hero headline"
                />
              )}
            </Heading>

            <Text variant={ETextStyleVariant.LargeRegular} className="mb-8 text-gray-70">
              {body ?? (
                <FormattedMessage
                  id="xox3d9"
                  defaultMessage="Need 5+ RVs for your event, film shoot, or corporate stay? We’re your dedicated, stress-free concierge service."
                  description="Group bookings hero body copy"
                />
              )}
            </Text>

            <Button
              variant={EButtonColorVariant.Primary}
              className="relative w-auto md:w-[300px]"
              label={defaultCtaLabel}
              // TODO: Wire up to actual enquiry flow / form when available
              onClick={() => {
                if (typeof window === 'undefined') return;
                const target = document.getElementById('group-bookings-form');
                target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            />
          </div>

          {/* Image collage */}
          <div className="mt-10 lg:mt-0 lg:flex-1">
            {images && images.length === 2 ? (
              <div className="relative inline-block mt-20 lg:mr-10 lg:pb-20 lg:pt-10">
                {/* Primary large image */}
                <div className="overflow-hidden rounded-2xl w-[200px] xs:w-[270px] md:w-[420px] lg:w-[420px] aspect-[497/347]">
                  <ResponsiveImage
                    src={images[0]!.src}
                    className="object-cover w-full h-full"
                    sizes="(min-width: 1024px) 420px, (min-width: 768px) 420px, (min-width: 640px) 270px, 200px"
                    sourceSizes={['landscape375', 'landscape768', 'landscape1080']}
                    progressive
                    withFetchPriority="low"
                    alt="Primary group RV rental collage"
                  />
                </div>

                {/* Secondary overlapping image */}
                <div className="absolute overflow-hidden shadow-lg rounded-2xl w-[130px] xs:w-[150px] sm:w-[192px] md:w-[224px] lg:w-[240px] aspect-[282/277] -right-16 -top-16 xs:-right-20 xs:-top-20 lg:-top-1 lg:-right-[170px]">
                  <ResponsiveImage
                    src={images[1]!.src}
                    className="object-cover w-full h-full"
                    sizes="(min-width: 1024px) 282px, (min-width: 768px) 224px, (min-width: 640px) 192px, 130px"
                    sourceSizes={['landscape280', 'landscape375', 'landscape572']}
                    progressive
                    withFetchPriority="low"
                    alt="Secondary group RV rental collage"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-12  grid-rows-2 lg:grid-rows-4 lg:gap-4 xs:gap-4 gap-2 md:gap-0">
                {/* Top left (Airstreams) — mobile: ~122×92, desktop: ~210×187 */}
                <div className="overflow-hidden xs:col-span-5 xs:col-start-2 xs:col-end-6 xs:row-start-1 xs:row-span-3 col-span-5 col-start-2 col-end-6 row-start-1 row-span-3 md:col-span-5 md:col-start-3 md:col-end-7 md:row-start-1 md:row-span-3 lg:mt-6 lg:col-end-7 rounded-2xl aspect-[122/92] lg:aspect-[210/187]">
                  <ResponsiveImage
                    src={HeroImageTertiary.src}
                    sizes="(min-width: 1280px) 22vw, (min-width: 1024px) 26vw, (min-width: 640px) 40vw, 70vw"
                    sourceSizes={['landscape375', 'landscape768', 'landscape1080']}
                    progressive
                    withFetchPriority="low"
                    alt="Row of Airstream RVs at a campsite"
                    className="object-cover w-full h-full rounded-2xl"
                  />
                </div>

                {/* Top right (construction site) — mobile: ~182×126, desktop: ~327×258 */}
                <div className="overflow-hidden xs:col-span-6 xs:translate-y-12 xs:row-span-2 xs:col-start-6 xs:col-end-13 xs:row-start-1 col-span-6 translate-y-9 row-span-2 col-start-6 col-end-13 row-start-1 md:col-span-5 md:translate-y-4 md:row-span-2 md:col-start-7 md:col-end-13 md:row-start-1 lg:row-start-2 lg:col-start-7 lg:col-end-13 lg:-translate-y-12 xl:-translate-y-16 rounded-2xl aspect-[182/126] lg:aspect-[327/258]">
                  <ResponsiveImage
                    src={HeroImagePrimary.src}
                    sizes="(min-width: 1280px) 26vw, (min-width: 1024px) 30vw, (min-width: 640px) 40vw, 80vw"
                    priority
                    withFetchPriority="high"
                    alt="RVs parked on a construction or work site"
                    className="object-cover w-full h-full rounded-2xl"
                  />
                </div>

                {/* Bottom left (wedding couple) — mobile: ~129×126, desktop: ~171×258 */}
                <div className="overflow-hidden xs:row-start-2 xs:row-span-6 xs:col-start-1 xs:col-end-5 xs:translate-y-10 row-start-2 row-span-6 col-start-1 col-end-5 translate-y-8 md:row-start-2 md:row-span-6 md:col-start-2 md:col-end-7 md:translate-y-14 md:ml-7 lg:col-start-2 lg:col-end-6 lg:translate-y-20 rounded-2xl aspect-[129/126] lg:aspect-[171/258]">
                  <ResponsiveImage
                    src={HeroImageSecondary.src}
                    sizes="(min-width: 1280px) 20vw, (min-width: 1024px) 24vw, (min-width: 640px) 38vw, 70vw"
                    sourceSizes={['portrait375', 'portrait768']}
                    progressive
                    withFetchPriority="low"
                    alt="Wedding couple standing beside an RV"
                    className="object-cover w-full h-full rounded-2xl"
                  />
                </div>

                {/* Bottom right (festival crowd) — mobile: ~175×76, desktop: ~298×153 */}
                <div className="overflow-hidden xs:col-span-6 xs:row-span-3 xs:col-start-5 xs:col-end-12 xs:row-start-4 xs:translate-y-6 col-span-7 row-span-3 col-start-5 col-end-12 row-start-4 translate-y-7 md:col-span-6 md:row-span-3 md:col-start-6 md:col-end-12 md:row-start-4 md:translate-y-6 lg:row-start-4 lg:-translate-y-12 xl:-translate-y-20 rounded-2xl aspect-[175/76] lg:aspect-[298/153]">
                  <ResponsiveImage
                    src={HeroImageQuaternary.src}
                    sizes="(min-width: 1280px) 24vw, (min-width: 1024px) 28vw, (min-width: 640px) 40vw, 80vw"
                    sourceSizes={['landscape375', 'landscape768', 'landscape1080']}
                    progressive
                    withFetchPriority="low"
                    alt="Festival crowd enjoying an outdoor RV event"
                    className="object-cover w-full h-full rounded-2xl"
                  />
                </div>
              </div>
            )}
          </div>
        </MaxWidth>
      </div>
    </section>
  );
};
